import { onBeforeUnmount, ref, type Ref } from "vue";

const MIN_WIDTH = 384;
const MAX_WIDTH = 960;

interface DragLifecycleOptions {
  // Receives the latest clientX; with throttle "frame", at most once per frame.
  onMove: (clientX: number) => void;
  // Runs after the drag ends, once the final position has been flushed.
  onTeardown?: () => void;
  throttle?: "frame";
  isResizing?: Ref<boolean>;
}

/**
 * The shared pointer-drag lifecycle. Teardown runs on mouseup, window blur,
 * unmount and on demand, because a mouseup is not guaranteed: alt-tabbing or
 * unmounting mid-drag would otherwise strand the body at cursor: col-resize
 * with the document listeners still bound.
 */
export function useDragLifecycle(options: DragLifecycleOptions) {
  let stopActiveDrag: (() => void) | null = null;

  function start(event: MouseEvent): void {
    event.preventDefault();
    stopActiveDrag?.();
    if (options.isResizing) options.isResizing.value = true;

    let frame = 0;
    let lastClientX = event.clientX;

    function flush(): void {
      frame = 0;
      options.onMove(lastClientX);
    }

    function onMouseMove(e: MouseEvent): void {
      lastClientX = e.clientX;
      if (options.throttle !== "frame") {
        options.onMove(lastClientX);
      } else if (!frame) {
        frame = requestAnimationFrame(flush);
      }
    }

    stopActiveDrag = (): void => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("blur", onWindowBlur);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // onTeardown before clearing the flag: the flag ungates the bars' CSS
      // transition, and a transition reads the after-change style. Restoring
      // it first would animate the release delta over 300ms while the panel
      // snaps.
      options.onTeardown?.();
      if (options.isResizing) options.isResizing.value = false;
      stopActiveDrag = null;
    };

    // Alt-tab, a native drag starting, or the pointer leaving the document all
    // swallow the mouseup. Without this the body keeps cursor: col-resize and
    // user-select: none globally, with the document listeners still bound.
    function onWindowBlur(): void {
      stopActiveDrag?.();
    }

    function onMouseUp(): void {
      // Flush, never discard: a throttled drag has a frame in flight holding
      // the last pointer position. Cancelling it loses up to a frame of
      // travel, and a flick that starts and ends inside one frame would lose
      // the whole drag — the scheduled frame never runs, so nothing is ever
      // applied.
      if (frame) {
        cancelAnimationFrame(frame);
        flush();
      }
      stopActiveDrag?.();
    }

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    window.addEventListener("blur", onWindowBlur);
  }

  onBeforeUnmount(() => stopActiveDrag?.());

  return { start, stop: () => stopActiveDrag?.() };
}

/**
 * Left-edge drag resize for a right-anchored panel. Returns the mousedown
 * handler and an element ref to measure the panel's rendered width (CSS
 * overrides can make it differ from the stored px width).
 */
export function usePanelResize(
  panelWidth: Ref<number>,
  isResizing: Ref<boolean>,
) {
  const panelRef = ref<HTMLElement | null>(null);
  let startX = 0;
  let startWidth = 0;

  const { start } = useDragLifecycle({
    isResizing,
    onMove(clientX) {
      const delta = startX - clientX;
      panelWidth.value = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, startWidth + delta),
      );
    },
  });

  function startResize(event: MouseEvent): void {
    startX = event.clientX;
    startWidth = panelRef.value?.offsetWidth ?? panelWidth.value;
    start(event);
  }

  return { panelRef, startResize };
}
